import React, { useEffect, useContext } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { GlobalStateContext } from "../../controller/GlobalState";
import { blogPosts } from "../../controller/global";
import ExploreOtherCaseStudy from "../../component/ExploreOtherCaseStudy";

const QUICK_FACTS = [
  { value: "AngularJS", label: "Frontend" },
  { value: ".NET Core API", label: "Backend" },
  { value: "Microsoft Azure", label: "Cloud Host" },
  { value: "SaaS", label: "Product Type" },
];

const TECH_STACK = [
  {
    title: "Frontend",
    items: ["AngularJS SPA", "Interactive dashboards", "Drag-and-drop goal structures", "Real-time visualizations", "No page reloads"],
  },
  {
    title: "Backend",
    items: [".NET Core REST API", "Strong typing & testability", "Goal hierarchies", "Condition tracking", "Automated reporting"],
  },
  {
    title: "Cloud & Integrations",
    items: ["Microsoft Azure hosting", "Google Workspace", "Office 365", "Slack", "Trello"],
  },
];

const CAPABILITY_PANELS = [
  { title: "Five Success Perspectives", items: ["Market", "Sales", "Operations", "Employees", "Economics"] },
  { title: "Goal Framework", items: ["Responsible owners", "Measurable KPIs", "Goal conditions", "Traffic-light status", "Automated tracking"] },
];

const KEY_FEATURES = [
  {
    title: "Goal Cascading",
    desc: "Overarching growth goals that cascade down into five strategic perspectives, ensuring every team-level objective connects to company direction.",
  },
  {
    title: "Real-Time Visibility",
    desc: "Interactive dashboards with traffic-light status indicators (red, yellow, green) showing exactly where intervention is needed.",
  },
  {
    title: "Condition Tracking",
    desc: "Organize goal conditions into structure, ability, and energy categories for comprehensive progress assessment.",
  },
  {
    title: "Automated Management",
    desc: "Automated reminders for status updates, activity surfacing, and progress logging that replace manual management overhead.",
  },
  {
    title: "Tool Integration",
    desc: "Seamless integration with Google Workspace, Office 365, Slack, and Trello to eliminate context-switching.",
  },
  {
    title: "Owner Assignment",
    desc: "Clear ownership and accountability with assigned goal owners and measurable units (KPIs or survey scores).",
  },
];

const KEY_BENEFITS = [
  "Unified environment for strategic oversight across the entire organization",
  "Reduced time spent chasing updates and manual status reports",
  "Full visibility into how team work connects to strategic goals",
  "Enabled faster, more confident decision-making across five success perspectives",
  "Accessible to all employees while powerful enough for serious strategic planning",
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the facts panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>
      {children}
    </MotionTag>
  );
}

const GoalEnvision = () => {
  const caseStudy = blogPosts.find((post) => post.slug === "goal-envision");
  const { setIsOpen } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "GoalEnvision Case Study | InQmise Technologies";
    const description = document.createElement("meta");
    description.name = "description";
    description.content = "Learn how we built GoalEnvision - a cloud-native SaaS platform that helps organizations set, align, and achieve strategic goals.";
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/case-studies/goal-envision");
  }, []);

  const openContactModal = () => setIsOpen(true);
  const goToCaseStudies = (e) => { e.preventDefault(); navigate("/case-studies"); };

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header
        style={caseStudy?.image ? {
          backgroundImage: `linear-gradient(rgba(251,252,248,.5),rgba(251,252,248,.8)), url(${caseStudy.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : undefined}
      >
        <div className="ea-hero ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <a className="ea-text-link" style={{ display: "inline-block", marginBottom: 14 }} href="/case-studies" onClick={goToCaseStudies}>← All Case Studies</a>
            <p className="ea-eyebrow">Case study · {caseStudy?.category}</p>
            <h1>GoalEnvision: strategy execution software that makes goals measurable</h1>
            <p className="ea-lede">
              Delivering a cloud-native GrowthOS that helps management teams set, align, and achieve their
              most important goals.
            </p>
            <div className="ea-actions">
              <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">Start your project ↗</button>
              <a className="ea-text-link" href="/case-studies" onClick={goToCaseStudies}>Explore other case studies</a>
            </div>
          </Reveal>

          <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
            <div className="ea-engine-top"><span /> GOALENVISION / QUICK FACTS <b>LIVE</b></div>
            {QUICK_FACTS.map((fact) => (
              <div className="cd-facts-row" key={fact.label}>
                <strong>{fact.value}</strong>
                <span>{fact.label}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </header>

      {/* THE CHALLENGE */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">The challenge</p>
          <h2 className="cd-h2">The Challenge</h2>
          <div className="std-overview">
            <p>
              Business leaders consistently struggle to bridge the gap between high-level strategy and day-to-
              day execution. Goals get defined in annual planning sessions, then lose momentum as they
              scatter across spreadsheets, email threads, and disconnected tools. Teams lack visibility into
              how their work connects to the company's direction, and management has no reliable way to
              track progress across the five dimensions that drive organizational success: market, sales,
              operations, employees, and finances. GoalEnvision was conceived to solve exactly this problem
              — giving every layer of an organization a shared, structured environment for turning ambition
              into achievement.
            </p>
          </div>
        </Reveal>
      </section>

      {/* WHY STRATEGY TOOLS FAIL */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">The domain</p>
            <h2 className="cd-h2">Why Strategy Tools Get Abandoned</h2>
            <div className="std-overview">
              <p>
                Almost every organization has tried a strategy tool. Most have stopped using one. The
                pattern is consistent enough that designing for it matters more than any individual
                feature: adoption is strong for a quarter, updates get patchier, and within a year the
                real conversation has moved back to a spreadsheet.
              </p>
              <p>
                <strong>The data-entry tax outweighs the payoff.</strong> Strategy tools ask people to
                maintain a second record of work they are already tracking elsewhere. The person doing
                the updating rarely benefits from them; the person reading the dashboard does. When
                effort and benefit sit with different people, the effort stops.
              </p>
              <p>
                <strong>Goals get defined so they can be reported, not so they can be achieved.</strong>
                Anything genuinely important is hard to measure, so targets drift toward whatever is
                easy to count. The dashboard turns green while the strategy stalls, which teaches
                everyone that the dashboard is theatre.
              </p>
              <p>
                <strong>Cascading loses meaning on the way down.</strong> A company goal is split across
                departments and split again across teams until the version an individual sees has no
                obvious connection to the ambition it came from. People cannot commit to an objective
                they cannot trace.
              </p>
              <p>
                <strong>Nothing explains why a goal is failing.</strong> A red status tells management
                that something is wrong, which they usually already knew. It does not say whether the
                team lacks a process, lacks a skill, or lacks the capacity to care right now &mdash; and
                those three problems have entirely different remedies.
              </p>
              <p>
                Each of these is a design problem rather than a technical one, and the platform&rsquo;s
                structure is a direct response to them.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT WE BUILT */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">What we built</p>
            <h2 className="cd-h2">What We Built</h2>
            <div className="std-overview">
              <p>
                We architected and delivered GoalEnvision as a fully cloud-hosted SaaS platform on Microsoft
                Azure, combining an AngularJS single-page application frontend with a .NET Core REST API
                backend. The Angular frontend delivers a rich, responsive experience — interactive
                dashboards, drag-and-drop goal structures, and real-time progress visualizations — without
                page reloads, keeping users immersed in their strategy rather than waiting on the browser.
              </p>
              <p>
                The .NET Core API provided a clean, maintainable service layer with strong typing and
                testability, enabling complex business logic around goal hierarchies, condition tracking, and
                automated reporting to be built with confidence.
              </p>
              <p>
                Azure's managed services underpinned the entire platform — providing scalable hosting,
                automated deployments, and enterprise-grade security that SaaS customers expect.
                Integrations were built for Google Workspace, Office 365, Slack, and Trello, so that discussions,
                tasks, and documents from tools teams already use could be linked directly to strategic goals
                and their conditions. This eliminated the context-switching tax that typically causes strategy
                tools to be abandoned.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TECHNOLOGY STACK */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Technology stack</p>
          <h2 className="cd-h2">Technology Stack</h2>
        </Reveal>
        <div className="ea-cards">
          {TECH_STACK.map((col, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-card" key={col.title}>
              <div>
                <h3>{col.title}</h3>
                <ul className="re-bare-list">
                  {col.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CORE PLATFORM CAPABILITIES */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Core platform capabilities</p>
            <h2 className="cd-h2">Core Platform Capabilities</h2>
            <div className="std-overview">
              <p>
                GoalEnvision's GrowthOS framework organizes every strategic goal across five success
                perspectives — market, sales, operations, employees, and economics — giving management a
                structured lens through which to assess the whole business at once. Each goal is assigned a
                responsible owner, a measurable unit (either an objective KPI or a subjective survey score), and
                a set of conditions categorized as structure, ability, or energy. A traffic-light status system (red,
                yellow, green) makes it immediately clear where the organization is on track and where
                intervention is needed, without requiring stakeholders to dig through reports.
              </p>
              <p>
                The platform also supports overarching growth goals that cascade down into the five
                perspectives, ensuring that every team-level objective is visibly connected to the company's top-
                line ambitions. Automated reminders prompt goal owners to update status, surface new
                activities, and log progress — replacing the manual chasing that typically consumes
                management time.
              </p>
            </div>
          </Reveal>
          <div className="ea-cards" style={{ gridTemplateColumns: "repeat(2,1fr)", marginTop: 32 }}>
            {CAPABILITY_PANELS.map((panel, i) => (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-card" key={panel.title}>
                <div>
                  <h3>{panel.title}</h3>
                  <ul className="re-bare-list">
                    {panel.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE CONDITIONS MODEL */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">The idea that makes it work</p>
          <h2 className="cd-h2">Conditions, Not Just Targets</h2>
          <div className="std-overview">
            <p>
              The most consequential design decision in the platform is that a goal is not only a number
              to hit. Every goal also carries a set of <strong>conditions</strong> &mdash; the things that
              must be true for it to be achievable at all &mdash; and each condition is classified as one of
              three kinds: <strong>structure</strong>, <strong>ability</strong>, or <strong>energy</strong>.
            </p>
            <p>
              That classification is what turns a status indicator into something actionable. A goal
              failing for structural reasons needs a process, a tool, or a decision that has not been
              made. A goal failing on ability needs training, hiring, or expertise the team does not yet
              have. A goal failing on energy needs attention, capacity, or motivation &mdash; often because
              the team is carrying too much at once. Those are three different conversations with three
              different owners, and a conventional red-amber-green indicator collapses all of them into
              one uninformative color.
            </p>
            <p>
              It also changes what happens during planning. Asking &ldquo;what must be true for this to
              work&rdquo; before committing to a target surfaces the missing prerequisite while there is still
              time to address it, rather than at the quarterly review when the number has already been
              missed.
            </p>
            <p>
              The same reasoning explains why a goal&rsquo;s measurable unit can be either an objective KPI or
              a subjective survey score. Insisting on hard numbers everywhere is what pushes
              organizations toward measuring the countable rather than the important. Some of what
              determines whether a strategy succeeds &mdash; team confidence, clarity of direction,
              customer sentiment &mdash; is real, consequential, and only honestly captured by asking
              people. Supporting both means the goal that matters can be tracked in the form it actually
              takes, instead of being reshaped until it fits the tool.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ENGINEERING A GOAL TREE */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">The engineering problem</p>
            <h2 className="cd-h2">What a Goal Hierarchy Demands of the System</h2>
            <div className="std-overview">
              <p>
                Goals that cascade are a tree, and trees make several ordinarily simple things
                complicated.
              </p>
              <p>
                <strong>Status has to roll up, and the rule is a judgment call.</strong> If a parent goal
                has four children and one is red, is the parent red? Averaging hides the failure;
                taking the worst case makes everything red permanently. The aggregation rule is a
                product decision with real consequences for whether anyone trusts the dashboard, and it
                has to be applied consistently at every level of a structure that users can rearrange by
                dragging.
              </p>
              <p>
                <strong>Mixed measurement units cannot be naively combined.</strong> A branch containing a
                revenue KPI, a percentage, and a survey score has no meaningful arithmetic mean. Rolling
                those up requires normalizing to progress against target rather than to the raw values,
                which is the only comparison that holds across units.
              </p>
              <p>
                <strong>Reorganizing the tree cannot rewrite history.</strong> Strategy structures change
                mid-year: goals get reassigned, merged, or moved under a different perspective. Progress
                recorded before a move still has to make sense after it, which means history belongs to
                the goal rather than to its position in the hierarchy.
              </p>
              <p>
                <strong>Integrations have to attach, not import.</strong> Linking discussions, tasks and
                documents from Google Workspace, Office 365, Slack and Trello removes the
                context-switching that kills adoption &mdash; but only if those tools remain the system of
                record for their own content. The platform references that work rather than copying it,
                so nobody maintains the same task in two places, which is the data-entry tax reappearing
                under a different name.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Key features</p>
          <h2 className="cd-h2">Key Features</h2>
        </Reveal>
        <div className="ea-cards">
          {KEY_FEATURES.map((feature, i) => (
            <Reveal as="div" i={(i % 3) + 1} reduceMotion={reduceMotion} className="ea-card" key={feature.title}>
              <div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUTCOMES & IMPACT */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">Outcomes & impact</p>
            <h2 className="cd-h2">Outcomes & Impact</h2>
            <div className="std-overview">
              <p>
                GoalEnvision launched as a production SaaS product serving management teams who
                previously had no single environment for strategic oversight. By combining a visually intuitive
                AngularJS interface with a robust .NET Core backend and the reliability of Azure's cloud
                infrastructure, we delivered a platform that is both powerful enough for serious strategic
                planning and accessible enough for every employee to engage with. Leaders gained back time
                previously spent chasing updates, and organizations gained the transparency needed to move
                faster and more confidently toward their goals.
              </p>
            </div>
            <p style={{ margin: "24px 0 12px", fontFamily: "Syne, sans-serif", fontSize: "var(--t-sm)", fontWeight: 700, letterSpacing: "-.03em" }}>Key Benefits</p>
            <ul className="re-bare-list">
              {KEY_BENEFITS.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* EXPLORE OTHER CASE STUDIES */}
      <ExploreOtherCaseStudy currentSlug="goal-envision" />

      {/* CTA */}
      <section className="ea-cta" style={{ textAlign: "center" }}>
        <div className="ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion} style={{ maxWidth: 560, margin: "0 auto" }}>
            <div className="ea-eyebrow" style={{ justifyContent: "center" }}>Ready when you are</div>
            <h2 style={{ margin: "16px 0" }}>Ready to Transform Your Strategy Into Action?</h2>
            <p style={{ margin: "0 auto 28px", maxWidth: 460 }}>
              Let's discuss how we can build sophisticated solutions that align your organization around shared goals.
            </p>
            <button className="ea-button ea-primary" onClick={openContactModal}>Get in Touch</button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default GoalEnvision;
